/** Pass 6.0c.3 — message edit/delete flag helpers.
 *
 *  Trivial truthy checks but factored out so:
 *    1. They have a single test surface (one file pinning the
 *       semantics).
 *    2. The ApiMessage type's defensive fallbacks (older docs
 *       may carry ``edited`` as Slack's raw {user,ts} dict
 *       before the 6.0c.3 migration backfills the boolean) are
 *       isolated in one place.
 *
 *  Once every doc in production has been migrated, the dict
 *  fallback is dead code — kept as a no-op safety net so a
 *  pre-migration row that escaped the backfill still renders
 *  with an indicator instead of going silent. */

import type { ApiMessage } from "./api";

/** True iff the message is marked edited. Accepts both the
 *  canonical boolean (6.0c.3+) and the legacy ApiEditMeta dict
 *  shape (pre-6.0c.3 sync writes / un-migrated rows). */
export function isMessageMarkedEdited(msg: ApiMessage): boolean {
  const v = msg.edited;
  if (typeof v === "boolean") return v;
  // dict / null / undefined — dict is truthy, null/undefined isn't.
  return Boolean(v);
}

/** True iff the message is marked deleted. Canonical boolean,
 *  default false. The viewer's renderer suppresses body/files/
 *  reactions when true and shows a [deleted] placeholder so the
 *  position in the conversation is preserved (matches Slack's
 *  own UI for deletions). */
export function isMessageMarkedDeleted(msg: ApiMessage): boolean {
  return msg.deleted === true;
}
